import moment from 'moment';
import {
  isPeriodOverPeriodChart,
  NEGATIVE_COLOR,
  POSITIVE_COLOR,
} from './period-over-period.helpers';
import { addOpacity, translateColor } from './dataset-color-config-builder';

export default (config, chartJSConfig, context) => {
  if (isPeriodOverPeriodChart(config)) {
    const {
      periodOverPeriod: { periodType },
    } = config;
    let tickConfig = getTickConfigByPeriodType(periodType) || {};
    if (tickConfig.type === 'DYNAMIC') {
      tickConfig = calculateDynamicTickConfig({ config });
    }
    chartJSConfig.data.datasets = extractPeriodOverPeriodDataSets(
      config,
      chartJSConfig.data.datasets,
      context,
      tickConfig
    );
    chartJSConfig.data.datasets = showFirstPeriodOverPeriodDataSets(
      chartJSConfig.data.datasets
    );
    chartJSConfig.options = setPeriodOverPeriodXAxis(
      tickConfig,
      chartJSConfig.options,
      chartJSConfig.data.datasets
    );
  } else {
    // in case any period over period styling are left over from previous iteration, remove so style doesnt look strange
    chartJSConfig.data.datasets = removePeriodOverPeriodStyle(
      chartJSConfig.data.datasets
    );
  }

  return chartJSConfig;
};

const convertMomentToDate = momentObject => {
  return new Date(momentObject.format('X') * 1000);
};

const setPeriodOverPeriodXAxis = (tickConfig, options, datasets) => {
  const minMaxXValues = datasets.reduce((minMaxXValues, dataset) => {
    // Parse with flexible format to handle both 'DD MMM YYYY' and 'DD MMM YYYY HH:mm'
    minMaxXValues[dataset.xAxisID] = {
      min: moment(dataset.data[0].x, ['DD MMM YYYY HH:mm', 'DD MMM YYYY']),
      max: moment(dataset.data[dataset.data.length - 1].x, [
        'DD MMM YYYY HH:mm',
        'DD MMM YYYY',
      ]),
    };
    return minMaxXValues;
  }, {});

  const current = {
    ...options.scales.x,
    axis: 'x',
    type: 'time',
    min: convertMomentToDate(minMaxXValues[POP_SCALES.CURRENT].min),
    max: convertMomentToDate(minMaxXValues[POP_SCALES.CURRENT].max),
    time: {
      unit: 'day',
      displayFormats: {
        day: 'YYYY-MM-DD', // should be dynamic
      },
    },
  };

  const tickMap = {};

  current.ticks.callback = function(value, index, ticks) {
    const { id } = this;

    if (index === 0) {
      tickMap[id] = {};
      // Parse flexibly to handle timestamps with or without time component
      tickMap[id].firstDate = moment(value);
      tickMap[id].counter = 1;
      return `${tickConfig.text} 1`;
    }
    //
    const currentDate = moment(value);

    if (
      currentDate
        .subtract(tickMap[id].counter * tickConfig.showEvery, tickConfig.unit)
        .isSame(tickMap[id].firstDate)
    ) {
      tickMap[id].counter++;

      return index < tickConfig.periodWindow
        ? `${tickConfig.text} ${tickMap[id].counter}`
        : '';
    }

    return null;
  };

  const previous = {
    ...options.scales.x,
    display: false,
    grid: {
      display: false,
    },
    axis: 'x',
    type: 'time',
    min: convertMomentToDate(minMaxXValues[POP_SCALES.PREVIOUS].min),
    max: convertMomentToDate(minMaxXValues[POP_SCALES.PREVIOUS].max),
    time: {
      unit: 'day',
      displayFormats: {
        day: 'YYYY-MM-DD', // should be dynamic
      },
    },
  };

  options.scales = {
    y: options.scales.y,
    [POP_SCALES.CURRENT]: current,
    [POP_SCALES.PREVIOUS]: previous,
  };

  return options;
};

const extractPeriodOverPeriodDataSets = (
  { series },
  datasets,
  context,
  tickConfig
) => {
  const popDatasets = [];
  series.map((series, index) => {
    const { period, label } = series.periodOverPeriod;
    const baseDataset = datasets[index];
    const hasPositiveChangeField =
      baseDataset.periodOverPeriod?.positiveChangeIsGood !== null;
    let dataset;

    if (period === POP_SCALES.PREVIOUS) {
      dataset = {
        ...baseDataset,
        label: label || tickConfig.PREVIOUS,
        data: [],
        borderDash: [5, 5],
        borderWidth: 2,
        xAxisID: POP_SCALES.PREVIOUS,
        fill: {
          target: '-1',
          above: 'transparent',
          below: 'transparent',
        },
      };
      if (hasPositiveChangeField) {
        const color = dataset.periodOverPeriod.positiveChangeIsGood
          ? NEGATIVE_COLOR
          : POSITIVE_COLOR;
        let translatedColor = translateColor(color, context);
        translatedColor = addOpacity(translatedColor, 0.4);
        dataset.fill.above = translatedColor;
      }
    } else if (period === POP_SCALES.CURRENT) {
      dataset = {
        ...baseDataset,
        label: label || tickConfig.CURRENT,
        data: [],
        xAxisID: POP_SCALES.CURRENT,
        fill: {
          below: 'transparent',
          above: 'transparent',
          target: '+1',
        },
      };

      if (hasPositiveChangeField) {
        const color = dataset.periodOverPeriod?.positiveChangeIsGood
          ? POSITIVE_COLOR
          : NEGATIVE_COLOR;
        let translatedColor = translateColor(color, context);
        translatedColor = addOpacity(translatedColor, 0.4);
        dataset.fill.above = translatedColor;
      }
    }

    dataset.data = series.aggregation.data.map(dataPoint => {
      const [timeDimension] = dataPoint.dimensions;
      const { value, formattedValue, dimensions, note } = dataPoint;
      return {
        x: timeDimension,
        y: value,
        yFormatted: formattedValue,
        aggregationLabel: dataset.label,
        dimensions,
        note,
      };
    });
    popDatasets.push(dataset);
  });

  return popDatasets;
};

const showFirstPeriodOverPeriodDataSets = datasets => {
  // Show only the first series, and it's previous period comparison. Mark the others as hidden.
  if (datasets.length <= 2) {
    return datasets;
  }
  return datasets.map((dataset, index) => {
    if (index > 1) {
      dataset.hidden = true;
    }
    return dataset;
  });
};

const removePeriodOverPeriodStyle = datasets => {
  return datasets.map(dataset => {
    /* eslint-disable */
    const { below, above, target, ...fill } = dataset.fill || {};
    dataset.fill = fill;
    return dataset;
  });
};

const POP_SCALES = {
  PREVIOUS: "PREVIOUS",
  CURRENT: "CURRENT",
};

const POP_TICK_CONFIG = {
  LAST_7_DAYS_OVER_LAST_7_TO_14_DAYS: {
    text: "DAY",
    unit: "day",
    [POP_SCALES.CURRENT]: "Last 7 days",
    [POP_SCALES.PREVIOUS]: "Previous 7 days",
    showEvery: 1,
    periodWindow: 7,
  },
  LAST_30_DAYS_OVER_LAST_30_TO_60_DAYS: {
    text: "WEEK",
    unit: "day",
    [POP_SCALES.CURRENT]: "Last 30 days",
    [POP_SCALES.PREVIOUS]: "Previous 30 days",
    showEvery: 7,
    periodWindow: 30,
  },
  LAST_90_DAYS_OVER_LAST_90_TO_180_DAYS: {
    text: "MONTH",
    unit: "month",
    [POP_SCALES.CURRENT]: "Last 90 days",
    [POP_SCALES.PREVIOUS]: "Previous 90 days",
    showEvery: 1,
    periodWindow: 90,
  },
  // DYNAMIC means that the above params - text, unit, period window will be calculated depending on the
  // selected time range
  PRECEDING_PERIOD: {
    type: "DYNAMIC",
  },
  SAME_PERIOD_PREVIOUS_YEAR: {
    type: "DYNAMIC",
  },
  SAME_PERIOD_PREVIOUS_MONTH: {
    type: "DYNAMIC",
  },
  LAST_CM_BF_WEEK: {
    type: "DYNAMIC",
  },
  SAME_PERIOD_PREVIOUS_YEAR_BFCM: {
    type: "DYNAMIC",
  },
};

const DYNAMIC_TICK_CONFIG = {
  LTE_WEEK: {
    text: "DAY",
    unit: "day",
    [POP_SCALES.CURRENT]: "Current",
    [POP_SCALES.PREVIOUS]: "Preceding",
    showEvery: 1,
  },
  LTE_MONTH: {
    text: "WEEK",
    unit: "day",
    [POP_SCALES.CURRENT]: "Current",
    [POP_SCALES.PREVIOUS]: "Preceding",
    showEvery: 7,
  },
  DEFAULT: {
    text: "MONTH",
    unit: "month",
    [POP_SCALES.CURRENT]: "Current",
    [POP_SCALES.PREVIOUS]: "Preceding",
    showEvery: 1,
  },
};

const getTickConfigByPeriodType = (periodType) => {
  if (POP_TICK_CONFIG[periodType]) {
    return POP_TICK_CONFIG[periodType];
  }
};

const numberOfDaysInSeries = ({ series }) => {
  const MILLI_IN_DAY = 1000 * 60 * 60 * 24;
  const { data } = series.aggregation;
  const first = new Date(data[0].dimensions[0]);
  const last = new Date(data[data.length - 1].dimensions[0]);
  const diff = last - first;
  return diff / MILLI_IN_DAY;
};

const calculateDynamicTickConfig = ({ config }) => {
  const [firstSeries] = config.series;
  const periodWindow = numberOfDaysInSeries({ series: firstSeries });
  if (periodWindow <= 7) {
    return { ...DYNAMIC_TICK_CONFIG.LTE_WEEK, periodWindow };
  } else if (periodWindow <= 30) {
    return { ...DYNAMIC_TICK_CONFIG.LTE_MONTH, periodWindow };
  }
  return { ...DYNAMIC_TICK_CONFIG.DEFAULT, periodWindow };
};
